import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { LoginResponse, OrgSelectionInfo, MFAMethod } from '../types/api.types';
import LoginForm from './LoginForm';
import EmailVerify from './EmailVerify';
import PasswordChangeRequired from './PasswordChangeRequired';
import MFASetup from './MFASetup';
import MFAVerify from './MFAVerify';
import MFAEnrollChoice from './MFAEnrollChoice';
import MFAResetFlow from './MFAResetFlow';
import OrgSelector from './OrgSelector';
import PasswordReset from './PasswordReset';
import { portalFromOrgCode } from '../utils/portal';

type AuthStep =
  | 'login'
  | 'email_verify'
  | 'password_change'
  | 'mfa_setup'
  | 'authenticator_offer'
  | 'mfa_enroll_otp'
  | 'mfa_verify'
  | 'mfa_reset'
  | 'org_select'
  | 'password_reset';

interface OAuthParams {
  returnUrl?: string;
  state?: string;
  codeChallenge?: string;
  codeChallengeMethod?: string;
}

export default function LoginPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { login } = useAuth();

  const [step, setStep] = useState<AuthStep>('login');
  const [sessionToken, setSessionToken] = useState<string>('');
  const [mfaMethod, setMfaMethod] = useState<MFAMethod | null>(null);
  // The finished login held while the optional authenticator step is on screen.
  const [pendingLogin, setPendingLogin] = useState<LoginResponse | null>(null);
  const [organizations, setOrganizations] = useState<OrgSelectionInfo[]>([]);
  const [oauthParams, setOAuthParams] = useState<OAuthParams>({});
  const [error, setError] = useState<string>('');
  const [version, setVersion] = useState<string>('');

  // Fetch version from auth backend (unauthenticated). Retry while the backend is still
  // starting up so a first-load race doesn't stick on "unknown".
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const fetchVersion = (attempt = 0) => {
      fetch('/api/auth/version')
        .then(r => { if (!r.ok) throw new Error(`http ${r.status}`); return r.json(); })
        .then(data => {
          if (cancelled) return;
          if (data && data.version) setVersion(data.version);
          else throw new Error('no version');
        })
        .catch(() => {
          if (cancelled) return;
          if (attempt < 10) timer = setTimeout(() => fetchVersion(attempt + 1), 2000);
          else setVersion('unknown');
        });
    };
    fetchVersion();
    return () => { cancelled = true; clearTimeout(timer); };
  }, []);

  // Parse OAuth params from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setOAuthParams({
      returnUrl: params.get('return_url') || undefined,
      state: params.get('state') || undefined,
      codeChallenge: params.get('code_challenge') || undefined,
      codeChallengeMethod: params.get('code_challenge_method') || undefined,
    });
  }, [location.search]);

  const handleLoginSuccess = (response: LoginResponse) => {
    setSessionToken(response.session_token);
    setError('');

    // Determine next step based on response
    if (response.requires_email_verification) {
      setStep('email_verify');
    } else if (response.requires_password_change) {
      setStep('password_change');
    } else if (response.requires_mfa_setup) {
      setStep('mfa_setup');
    } else if (response.requires_mfa) {
      setMfaMethod(response.mfa_method ?? null);
      setStep('mfa_verify');
    } else if (response.requires_org_selection && response.organizations) {
      setOrganizations(response.organizations);
      setStep('org_select');
    } else {
      // Login complete - no additional steps
      completeAuth(response);
    }
  };

  const handleEmailVerifySuccess = (response: LoginResponse) => {
    setSessionToken(response.session_token);

    if (response.requires_password_change) {
      setStep('password_change');
    } else if (response.requires_mfa_setup) {
      setStep('mfa_setup');
    } else if (response.requires_mfa) {
      setMfaMethod(response.mfa_method ?? null);
      setStep('mfa_verify');
    } else if (response.requires_org_selection && response.organizations) {
      setOrganizations(response.organizations);
      setStep('org_select');
    } else {
      completeAuth(response);
    }
  };

  const handlePasswordChangeSuccess = (response: LoginResponse) => {
    setSessionToken(response.session_token);

    if (response.requires_mfa_setup) {
      setStep('mfa_setup');
    } else if (response.requires_mfa) {
      setMfaMethod(response.mfa_method ?? null);
      setStep('mfa_verify');
    } else if (response.requires_org_selection && response.organizations) {
      setOrganizations(response.organizations);
      setStep('org_select');
    } else {
      completeAuth(response);
    }
  };

  const handleMFASetupSuccess = (response: LoginResponse) => {
    setSessionToken(response.session_token);

    if (response.requires_org_selection && response.organizations) {
      setOrganizations(response.organizations);
      setStep('org_select');
    } else {
      completeAuth(response);
    }
  };

  const handleMFAVerifySuccess = (response: LoginResponse) => {
    // Only update sessionToken if the response includes one
    // (direct flow response may only have access_token, not session_token)
    if (response.session_token) {
      setSessionToken(response.session_token);
    }

    if (response.requires_org_selection && response.organizations) {
      setOrganizations(response.organizations);
      setStep('org_select');
    } else {
      completeAuth(response);
    }
  };

  const handleOrgSelectSuccess = (response: LoginResponse) => {
    completeAuth(response);
  };

  /**
   * Get the frontend path based on organization code
   * An SSC account -> /ssc/, a PCF account -> /pcf/, others -> /org/
   */
  const getOrgFrontendPath = (orgCode: string): string => {
    return `/app/${portalFromOrgCode(orgCode) ?? 'org'}/`;
  };

  const completeAuth = async (response: LoginResponse) => {
    // Use session_token from response, or fall back to stored sessionToken state
    // (MFA verify direct flow may return access_token without session_token)
    const effectiveSessionToken = response.session_token || sessionToken;

    // If OAuth flow with PKCE params, get authorization code and redirect
    // All PKCE params (return_url, state, code_challenge) are required
    if (oauthParams.returnUrl && oauthParams.state && oauthParams.codeChallenge) {
      try {
        const authResponse = await apiService.authorize({
          session_token: effectiveSessionToken,
          return_url: oauthParams.returnUrl,
          state: oauthParams.state,
          code_challenge: oauthParams.codeChallenge,
          code_challenge_method: oauthParams.codeChallengeMethod || 'S256',
        });

        // Redirect to return URL with auth code (use redirect_url from response)
        window.location.href = authResponse.redirect_url;
        return;
      } catch (err) {
        setError(apiService.getErrorMessage(err));
        return;
      }
    }

    // If return_url exists but missing PKCE params, show error
    if (oauthParams.returnUrl && (!oauthParams.state || !oauthParams.codeChallenge)) {
      setError('OAuth flow requires state and code_challenge parameters');
      return;
    }

    // Cookie-based auth flow - redirect to org-specific frontend
    // Session cookie is already set by the backend during login
    if (response.user?.current_organization?.code) {
      const frontendPath = getOrgFrontendPath(response.user.current_organization.code);
      console.log('[LoginPage] Redirecting to org frontend:', frontendPath);
      window.location.href = frontendPath;
      return;
    }

    // Direct login to Argus settings (for admin users without org)
    if (response.access_token && response.user) {
      login(response.access_token, response.user);
      navigate('/');
      return;
    }

    // Fallback: If we have session_token but no access_token, exchange it
    if (effectiveSessionToken) {
      try {
        const tokenResponse = await apiService.getAccessToken(effectiveSessionToken);
        // If user has org, redirect to frontend
        if (tokenResponse.user?.current_organization?.code) {
          const frontendPath = getOrgFrontendPath(tokenResponse.user.current_organization.code);
          console.log('[LoginPage] Redirecting to org frontend:', frontendPath);
          window.location.href = frontendPath;
          return;
        }
        // Otherwise stay in Argus (admin user)
        login(tokenResponse.access_token, tokenResponse.user);
        navigate('/');
      } catch (err) {
        setError(apiService.getErrorMessage(err));
      }
    }
  };

  const handleMFAResetRequest = () => {
    setStep('mfa_reset');
  };

  const handleMFAResetComplete = () => {
    setStep('login');
  };

  const handleForgotPassword = () => {
    setStep('password_reset');
  };

  // A completed reset is also a completed login — the emailed code proved the mailbox and
  // the user just set the password. Route it exactly like any other finished sign-in
  // rather than sending them back to the form to collect a second emailed code.
  const handlePasswordResetComplete = (response: LoginResponse) => {
    if (response.requires_org_selection && response.organizations) {
      if (response.session_token) setSessionToken(response.session_token);
      setOrganizations(response.organizations);
      setStep('org_select');
      return;
    }

    // The account is usable at this point. Offer the authenticator as an extra, holding
    // the finished login so declining can complete it without another round trip.
    if (response.mfa_setup_token) {
      setSessionToken(response.mfa_setup_token);
      setPendingLogin(response);
      setStep('authenticator_offer');
      return;
    }

    if (response.session_token) setSessionToken(response.session_token);
    completeAuth(response);
  };

  const handleAuthenticatorChoice = (wantsAuthenticator: boolean) => {
    if (wantsAuthenticator) {
      setStep('mfa_enroll_otp');
    } else if (pendingLogin) {
      completeAuth(pendingLogin);
    }
  };

  const handleBackToLogin = () => {
    setStep('login');
    setSessionToken('');
    // Drop any held login too, so returning to the form can never complete a sign-in
    // that belongs to an earlier attempt.
    setPendingLogin(null);
    setError('');
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Logo/Title */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900">MAAP</h1>
            <p className="text-gray-600 mt-2">Modernized Approvals & Authorizations Process</p>
          </div>

        {/* Error Display */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded text-red-700">
            {error}
          </div>
        )}

        {/* Auth Steps */}
        {step === 'login' && (
          <LoginForm
            onSuccess={handleLoginSuccess}
            onForgotPassword={handleForgotPassword}
          />
        )}

        {step === 'email_verify' && (
          <EmailVerify
            sessionToken={sessionToken}
            onSuccess={handleEmailVerifySuccess}
            onBack={handleBackToLogin}
          />
        )}

        {step === 'password_change' && (
          <PasswordChangeRequired
            sessionToken={sessionToken}
            onSuccess={handlePasswordChangeSuccess}
            onBack={handleBackToLogin}
          />
        )}

        {step === 'mfa_setup' && (
          <MFASetup
            sessionToken={sessionToken}
            onSuccess={handleMFASetupSuccess}
            onBack={handleBackToLogin}
          />
        )}

        {step === 'authenticator_offer' && (
          <MFAEnrollChoice onChoose={handleAuthenticatorChoice} />
        )}

        {step === 'mfa_enroll_otp' && (
          <MFASetup
            sessionToken={sessionToken}
            enrollMode
            onSuccess={handleMFASetupSuccess}
            onBack={() => setStep('authenticator_offer')}
          />
        )}

        {step === 'mfa_verify' && (
          <MFAVerify
            sessionToken={sessionToken}
            mfaMethod={mfaMethod}
            onSuccess={handleMFAVerifySuccess}
            onMFAReset={handleMFAResetRequest}
            onBack={handleBackToLogin}
          />
        )}

        {step === 'mfa_reset' && (
          <MFAResetFlow
            onComplete={handleMFAResetComplete}
            onBack={handleBackToLogin}
          />
        )}

        {step === 'org_select' && (
          <OrgSelector
            sessionToken={sessionToken}
            organizations={organizations}
            returnUrl={oauthParams.returnUrl}
            onSuccess={handleOrgSelectSuccess}
            onBack={handleBackToLogin}
          />
        )}

        {step === 'password_reset' && (
          <PasswordReset
            onComplete={handlePasswordResetComplete}
            onBack={handleBackToLogin}
          />
        )}
        </div>
      </div>

      {/* Sticky Footer */}
      <footer className="py-3 text-center text-xs bg-gray-700">
        <p className={version.includes('rc') ? 'text-red-400' : 'text-white'}>
          {version || 'loading...'} | US Space Force
        </p>
      </footer>
    </div>
  );
}
